import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  RULE_MATCH_FIELDS,
  RULE_MATCH_TYPES,
  type BankRule,
  type BankRuleSplit,
  type CreateBankRuleInput,
  type RuleMatchField,
  type RuleMatchType,
  type RuleSuggestion,
} from '@accounting/shared';
import {
  useBankAccounts,
  useBankRules,
  useCreateBankRule,
  useDeactivateBankRule,
  useReorderBankRules,
  useRuleSuggestions,
  useUpdateBankRule,
} from '../../hooks/useBank';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { useContacts } from '../../hooks/useContacts';
import { useClasses, useLocations } from '../../hooks/useDimensions';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

// Plain-English labels for the operator dropdown and the rule summary. The
// enum values are the API's; "starts_with" in a <select> read as a code, not a
// choice, and the owner asked for a condition that already existed.
const MATCH_TYPE_LABELS: Record<RuleMatchType, string> = {
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  exact: 'is exactly',
  regex: 'matches regex',
};

export const Route = createFileRoute('/_auth/bank/rules')({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    fromTx?: string;
    prefillName?: string;
    prefillMatchValue?: string;
    prefillAmount?: string;
    prefillBankAccountId?: string;
    prefillTargetAccountId?: string;
  } => ({
    fromTx: typeof search.fromTx === 'string' ? search.fromTx : undefined,
    prefillName: typeof search.prefillName === 'string' ? search.prefillName : undefined,
    prefillMatchValue: typeof search.prefillMatchValue === 'string' ? search.prefillMatchValue : undefined,
    prefillAmount: typeof search.prefillAmount === 'string' ? search.prefillAmount : undefined,
    prefillBankAccountId: typeof search.prefillBankAccountId === 'string' ? search.prefillBankAccountId : undefined,
    prefillTargetAccountId: typeof search.prefillTargetAccountId === 'string' ? search.prefillTargetAccountId : undefined,
  }),
  component: BankRulesPage,
});

function BankRulesPage() {
  const companyId = useActiveCompanyId();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data: rules = [], isLoading } = useBankRules(companyId, { includeInactive });
  // If we arrived from a transaction's "Create rule" link, open the form
  // immediately pre-filled. Otherwise default to closed.
  const [showCreate, setShowCreate] = useState(!!search.prefillMatchValue);
  const [editing, setEditing] = useState<BankRule | null>(null);

  const prefill = search.prefillMatchValue
    ? {
        name: search.prefillName ?? '',
        matchValue: search.prefillMatchValue,
        amount: search.prefillAmount,
        bankAccountId: search.prefillBankAccountId,
        targetAccountId: search.prefillTargetAccountId,
      }
    : undefined;

  // After the form opens (or the user dismisses), clear the search params so a
  // refresh doesn't re-pop the same prefill.
  const clearPrefillParams = () =>
    void navigate({
      search: () => ({}),
      replace: true,
    });

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  return (
    <div>
      <Link to="/bank" style={{ fontSize: 14 }}>← Bank accounts</Link>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Bank rules</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Rules match incoming bank transactions to a GL account, evaluated
            top-to-bottom. First match wins. Auto-post creates the journal entry
            automatically; suggest-only leaves the match for review.
          </p>
        </div>
        {!showCreate && !editing && (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New rule
          </button>
        )}
      </div>

      <label
        style={{
          display: 'inline-flex',
          gap: 6,
          alignItems: 'center',
          color: 'var(--text-muted)',
          fontSize: 14,
          marginBottom: 16,
        }}
      >
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
        />
        Include inactive rules
      </label>

      {showCreate && (
        <RuleForm
          companyId={companyId}
          prefill={prefill}
          onClose={() => {
            setShowCreate(false);
            clearPrefillParams();
          }}
        />
      )}
      {editing && (
        <RuleForm
          companyId={companyId}
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {!isLoading && rules.length === 0 && !showCreate && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No rules yet.</p>
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            Create your first rule
          </button>
        </div>
      )}

      {rules.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>#</th>
              <th>Name</th>
              <th>Match</th>
              <th>Target</th>
              <th>Mode</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((r, idx) => (
              <RuleRow
                key={r.id}
                rule={r}
                rules={rules}
                index={idx}
                companyId={companyId}
                onEdit={() => setEditing(r)}
              />
            ))}
          </tbody>
        </table>
      )}

      <SuggestedRules companyId={companyId} />
    </div>
  );
}

function SuggestedRules({ companyId }: { companyId: string }) {
  const { data: suggestions = [], isLoading } = useRuleSuggestions(companyId);
  const create = useCreateBankRule(companyId);
  const [creatingKey, setCreatingKey] = useState<string | null>(null);

  if (isLoading || suggestions.length === 0) return null;

  const createFrom = (s: RuleSuggestion) => {
    setCreatingKey(s.key);
    const input: CreateBankRuleInput = {
      name: s.matchValue.replace(/\b\w/g, (c) => c.toUpperCase()),
      bankAccountId: null,
      matchField: 'description_or_merchant',
      matchType: 'contains',
      matchValue: s.matchValue,
      targetAccountId: s.targetAccountId,
      targetContactId: s.suggestedContactId ?? null,
      targetLocationId: null,
      targetClassId: null,
      autoPost: false,
    };
    create.mutate(input, { onSettled: () => setCreatingKey(null) });
  };

  return (
    <div style={{ marginTop: 28 }}>
      <h2 style={{ fontSize: 18, marginBottom: 4 }}>Suggested from history</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        Patterns the system noticed in how you categorized past transactions. Create a rule to
        auto-apply the category going forward (added as suggest-only — flip to auto-post when you
        trust it).
      </p>
      <table className="table">
        <thead>
          <tr>
            <th>Pattern</th>
            <th>Suggested account</th>
            <th className="right" style={{ width: 90 }}>Seen</th>
            <th style={{ width: 1 }}></th>
          </tr>
        </thead>
        <tbody>
          {suggestions.map((s) => (
            <tr key={s.key}>
              <td>
                <div style={{ fontWeight: 500 }}>contains "{s.matchValue}"</div>
                {s.sampleDescriptions.length > 0 && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    e.g. {s.sampleDescriptions[0]}
                  </div>
                )}
              </td>
              <td>
                <span style={{ fontFamily: 'ui-monospace, monospace', color: 'var(--text-muted)' }}>
                  {s.accountCode}
                </span>{' '}
                {s.accountName}
                {s.contactName && <div className="muted" style={{ fontSize: 12 }}>Contact: {s.contactName}</div>}
              </td>
              <td className="right">
                {s.count}× <span className="muted">({Math.round(s.confidence * 100)}%)</span>
              </td>
              <td>
                <button
                  className="btn-primary"
                  disabled={create.isPending && creatingKey === s.key}
                  onClick={() => createFrom(s)}
                >
                  {create.isPending && creatingKey === s.key ? 'Creating…' : 'Create rule'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RuleRow({
  rule,
  rules,
  index,
  companyId,
  onEdit,
}: {
  rule: BankRule;
  rules: BankRule[];
  index: number;
  companyId: string;
  onEdit: () => void;
}) {
  const { data: accounts = [] } = useAccounts(companyId);
  const reorder = useReorderBankRules(companyId);
  const deactivate = useDeactivateBankRule(companyId);
  const update = useUpdateBankRule(companyId);
  const account = accounts.find((a) => a.id === rule.targetAccountId);

  const moveUp = () => {
    if (index === 0) return;
    const ids = rules.map((r) => r.id);
    [ids[index - 1], ids[index]] = [ids[index]!, ids[index - 1]!];
    reorder.mutate({ orderedIds: ids });
  };
  const moveDown = () => {
    if (index === rules.length - 1) return;
    const ids = rules.map((r) => r.id);
    [ids[index + 1], ids[index]] = [ids[index]!, ids[index + 1]!];
    reorder.mutate({ orderedIds: ids });
  };

  return (
    <tr style={{ opacity: rule.isActive ? 1 : 0.6 }}>
      <td style={{ color: 'var(--text-muted)' }}>{rule.priority}</td>
      <td style={{ fontWeight: 500 }}>{rule.name}</td>
      <td className="muted" style={{ fontSize: 13 }}>
        <div>{describeMatch(rule)}</div>
      </td>
      <td>
        {account ? (
          <span>
            <span style={{ fontFamily: 'ui-monospace, monospace', color: 'var(--text-muted)' }}>
              {account.code}
            </span>{' '}
            {account.name}
          </span>
        ) : (
          <span className="muted">unknown</span>
        )}
        {(rule.splits ?? []).map((s, i) => {
          const sa = accounts.find((a) => a.id === s.accountId);
          return (
            <div key={i} className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              ↳ {s.kind === 'percent' ? `${s.value}%` : formatCurrency(s.value)} to{' '}
              {sa ? `${sa.code} ${sa.name}` : 'unknown'}
            </div>
          );
        })}
        {(rule.splits?.length ?? 0) > 0 && (
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>↳ anything left to the target</div>
        )}
      </td>
      <td>
        {rule.autoPost ? (
          <span className="tag ok">auto-post</span>
        ) : (
          <span className="tag">suggest</span>
        )}
        {rule.requireReviewAboveAmount && rule.autoPost && (
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            ceiling {formatCurrency(rule.requireReviewAboveAmount)}
          </div>
        )}
        {!rule.isActive && <span className="tag bad" style={{ marginLeft: 6 }}>inactive</span>}
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <button
          className="btn-secondary"
          onClick={moveUp}
          disabled={index === 0 || reorder.isPending}
          style={{ marginRight: 4 }}
          title="Move up"
        >
          ↑
        </button>
        <button
          className="btn-secondary"
          onClick={moveDown}
          disabled={index === rules.length - 1 || reorder.isPending}
          style={{ marginRight: 6 }}
          title="Move down"
        >
          ↓
        </button>
        <button className="btn-secondary" onClick={onEdit} style={{ marginRight: 6 }}>
          Edit
        </button>
        {rule.isActive ? (
          <button
            className="btn-secondary"
            onClick={() => {
              if (confirm(`Deactivate rule "${rule.name}"?`)) deactivate.mutate(rule.id);
            }}
          >
            Deactivate
          </button>
        ) : (
          <button
            className="btn-secondary"
            onClick={() => update.mutate({ id: rule.id, input: { isActive: true } })}
          >
            Reactivate
          </button>
        )}
      </td>
    </tr>
  );
}

function describeMatch(rule: BankRule): string {
  const fieldLabel = (() => {
    switch (rule.matchField) {
      case 'description': return 'description';
      case 'merchant_name': return 'merchant';
      case 'description_or_merchant': return 'description or merchant';
      case 'description_and_amount': return 'description + amount';
      case 'amount_range': return 'amount';
    }
  })();

  const parts: string[] = [];
  if (rule.matchType && rule.matchValue) {
    parts.push(`${fieldLabel} ${MATCH_TYPE_LABELS[rule.matchType]} "${rule.matchValue}"`);
  }
  if (rule.amountMin || rule.amountMax) {
    if (rule.amountMin && rule.amountMin === rule.amountMax) {
      parts.push(`amount = ${formatCurrency(rule.amountMin)}`);
    } else {
      const min = rule.amountMin ? formatCurrency(rule.amountMin) : '−∞';
      const max = rule.amountMax ? formatCurrency(rule.amountMax) : '∞';
      parts.push(`amount in [${min}, ${max}]`);
    }
  }
  return parts.join(' AND ') || fieldLabel;
}

interface RulePrefill {
  name?: string;
  matchValue?: string;
  amount?: string;
  bankAccountId?: string;
  targetAccountId?: string;
}

function RuleForm({
  companyId,
  existing,
  prefill,
  onClose,
}: {
  companyId: string;
  existing?: BankRule;
  prefill?: RulePrefill;
  onClose: () => void;
}) {
  const create = useCreateBankRule(companyId);
  const update = useUpdateBankRule(companyId);
  const { data: bankAccountsData } = useBankAccounts(companyId);
  const bankAccounts = bankAccountsData?.bankAccounts ?? [];
  const { data: accounts = [] } = useAccounts(companyId);
  const { data: contacts = [] } = useContacts(companyId);
  const { data: locations = [] } = useLocations(companyId);
  const { data: classes = [] } = useClasses(companyId);
  const isDirectPostable = (a: typeof accounts[number]) => a.isActive && a.allowDirectPosting;
  const isEditing = !!existing;

  // When prefill is present (user clicked "Create rule" from a transaction),
  // seed sensible defaults: description-and-amount match, exact amount, target
  // account from the AI suggestion, name from the merchant.
  const prefillAmount = prefill?.amount && parseFloat(prefill.amount) > 0 ? prefill.amount : '';

  const [form, setForm] = useState({
    name: existing?.name ?? prefill?.name ?? '',
    bankAccountId: existing?.bankAccountId ?? prefill?.bankAccountId ?? '',
    matchField: (existing?.matchField ?? (prefill ? 'description_and_amount' : 'description_or_merchant')) as RuleMatchField,
    matchType: (existing?.matchType ?? 'contains') as RuleMatchType,
    matchValue: existing?.matchValue ?? prefill?.matchValue ?? '',
    amountMin: existing?.amountMin ?? prefillAmount,
    amountMax: existing?.amountMax ?? prefillAmount,
    amountMode: (
      existing
        ? existing.amountMin && existing.amountMin === existing.amountMax ? 'exact' : 'range'
        : prefillAmount ? 'exact' : 'range'
    ) as 'range' | 'exact',
    targetAccountId: existing?.targetAccountId ?? prefill?.targetAccountId ?? '',
    targetContactId: existing?.targetContactId ?? '',
    targetLocationId: existing?.targetLocationId ?? '',
    targetClassId: existing?.targetClassId ?? '',
    memoTemplate: existing?.memoTemplate ?? '',
    autoPost: existing?.autoPost ?? false,
    requireReviewAboveAmount: existing?.requireReviewAboveAmount ?? '',
  });
  // Extra lines carved off each match before the target takes the remainder.
  const [splits, setSplits] = useState<{ accountId: string; kind: 'percent' | 'amount'; value: string; memo: string }[]>(
    (existing?.splits ?? []).map((s) => ({ accountId: s.accountId, kind: s.kind, value: s.value, memo: s.memo ?? '' })),
  );
  const percentTotal = splits
    .filter((s) => s.kind === 'percent')
    .reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
  const splitsValid =
    splits.every((s) => s.accountId && (parseFloat(s.value) || 0) > 0 && (s.kind !== 'percent' || parseFloat(s.value) <= 100)) &&
    percentTotal <= 100;

  const showText =
    form.matchField === 'description' ||
    form.matchField === 'merchant_name' ||
    form.matchField === 'description_or_merchant' ||
    form.matchField === 'description_and_amount';
  const showAmount =
    form.matchField === 'amount_range' || form.matchField === 'description_and_amount';

  const mutation = isEditing ? update : create;
  const errorMessage =
    mutation.error instanceof ApiError
      ? (mutation.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : mutation.error?.message;

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const buildInput = (): CreateBankRuleInput => ({
    name: form.name.trim(),
    bankAccountId: form.bankAccountId || null,
    matchField: form.matchField,
    matchType: showText ? form.matchType : undefined,
    matchValue: showText ? form.matchValue.trim() : undefined,
    amountMin: showAmount && form.amountMin ? form.amountMin : undefined,
    amountMax: showAmount
      ? form.amountMode === 'exact'
        ? form.amountMin || undefined
        : form.amountMax || undefined
      : undefined,
    targetAccountId: form.targetAccountId,
    targetContactId: form.targetContactId || null,
    targetLocationId: form.targetLocationId || null,
    targetClassId: form.targetClassId || null,
    memoTemplate: form.memoTemplate.trim() || undefined,
    splits: splits.map<BankRuleSplit>((s) => ({
      accountId: s.accountId,
      kind: s.kind,
      value: s.value.trim(),
      memo: s.memo.trim() || null,
    })),
    autoPost: form.autoPost,
    requireReviewAboveAmount: form.autoPost && form.requireReviewAboveAmount
      ? form.requireReviewAboveAmount
      : undefined,
  });

  return (
    <form
      className="card"
      style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        const input = buildInput();
        if (isEditing) {
          update.mutate({ id: existing.id, input }, { onSuccess: () => onClose() });
        } else {
          create.mutate(input, { onSuccess: () => onClose() });
        }
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 18 }}>
        {isEditing ? `Edit rule "${existing.name}"` : 'New rule'}
      </h2>

      <div className="form-grid">
        <label htmlFor="r-name">Name *</label>
        <input
          id="r-name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          required
          autoFocus
        />

        <label htmlFor="r-scope">Scope</label>
        <select
          id="r-scope"
          value={form.bankAccountId}
          onChange={(e) => set('bankAccountId', e.target.value)}
        >
          <option value="">All bank accounts</option>
          {bankAccounts.map((b) => (
            <option key={b.id} value={b.id}>{b.accountName}</option>
          ))}
        </select>

        <label htmlFor="r-field">Match on</label>
        <select
          id="r-field"
          value={form.matchField}
          onChange={(e) => set('matchField', e.target.value as RuleMatchField)}
        >
          {RULE_MATCH_FIELDS.map((f) => (
            <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>
          ))}
        </select>

        {showText && (
          <>
            <label htmlFor="r-type">Operator</label>
            <select
              id="r-type"
              value={form.matchType}
              onChange={(e) => set('matchType', e.target.value as RuleMatchType)}
            >
              {RULE_MATCH_TYPES.map((t) => (
                <option key={t} value={t}>{MATCH_TYPE_LABELS[t]}</option>
              ))}
            </select>

            <label htmlFor="r-value">Value *</label>
            <input
              id="r-value"
              value={form.matchValue}
              onChange={(e) => set('matchValue', e.target.value)}
              placeholder={form.matchType === 'regex' ? 'starbucks\\s+\\d+' : 'starbucks'}
              required
            />
          </>
        )}

        {showAmount && (
          <>
            <label htmlFor="r-amode">Amount match</label>
            <select
              id="r-amode"
              value={form.amountMode}
              onChange={(e) => set('amountMode', e.target.value as 'range' | 'exact')}
              style={{ maxWidth: 200 }}
            >
              <option value="range">Range</option>
              <option value="exact">Exact amount</option>
            </select>

            {form.amountMode === 'exact' ? (
              <>
                <label htmlFor="r-aexact">Amount</label>
                <input
                  id="r-aexact"
                  type="text"
                  inputMode="decimal"
                  value={form.amountMin}
                  onChange={(e) => set('amountMin', e.target.value)}
                  placeholder="0.00"
                  style={{ maxWidth: 200 }}
                />
              </>
            ) : (
              <>
                <label htmlFor="r-amin">Amount min</label>
                <input
                  id="r-amin"
                  type="text"
                  inputMode="decimal"
                  value={form.amountMin}
                  onChange={(e) => set('amountMin', e.target.value)}
                  placeholder="0.00"
                  style={{ maxWidth: 200 }}
                />

                <label htmlFor="r-amax">Amount max</label>
                <input
                  id="r-amax"
                  type="text"
                  inputMode="decimal"
                  value={form.amountMax}
                  onChange={(e) => set('amountMax', e.target.value)}
                  placeholder="1000.00"
                  style={{ maxWidth: 200 }}
                />
              </>
            )}
          </>
        )}

        <label htmlFor="r-acct">Target account *</label>
        <AccountPicker
          id="r-acct"
          accounts={accounts}
          value={form.targetAccountId}
          onChange={(id) => set('targetAccountId', id)}
          predicate={isDirectPostable}
          required
        />

        <label>Split</label>
        <div>
          {splits.length > 0 && (
            <p className="muted" style={{ margin: '0 0 6px', fontSize: 12 }}>
              Each line below is carved off every matched amount first; the target account
              takes whatever is left, so the entry always nets to the transaction.
            </p>
          )}
          {splits.map((row, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px' }}>
                <AccountPicker
                  accounts={accounts}
                  value={row.accountId}
                  onChange={(id) => setSplits((p) => p.map((r, j) => (j === i ? { ...r, accountId: id } : r)))}
                  predicate={isDirectPostable}
                />
              </div>
              <select
                value={row.kind}
                onChange={(e) =>
                  setSplits((p) => p.map((r, j) => (j === i ? { ...r, kind: e.target.value as 'percent' | 'amount' } : r)))
                }
                style={{ width: 110 }}
              >
                <option value="amount">Fixed $</option>
                <option value="percent">Percent</option>
              </select>
              <input
                inputMode="decimal"
                value={row.value}
                onChange={(e) => setSplits((p) => p.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)))}
                placeholder={row.kind === 'percent' ? '50' : '1250.00'}
                style={{ width: 100, textAlign: 'right' }}
              />
              <input
                value={row.memo}
                onChange={(e) => setSplits((p) => p.map((r, j) => (j === i ? { ...r, memo: e.target.value } : r)))}
                placeholder="line memo"
                style={{ width: 140 }}
              />
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setSplits((p) => p.filter((_, j) => j !== i))}
                title="Remove line"
              >
                ×
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="btn-secondary"
              disabled={splits.length >= 10}
              onClick={() => setSplits((p) => [...p, { accountId: '', kind: 'amount', value: '', memo: '' }])}
            >
              + Split line
            </button>
            {splits.length > 0 && (
              <span className={splitsValid ? 'muted' : 'field-error'} style={{ fontSize: 12 }}>
                {percentTotal > 0 ? `${percentTotal}% by percent, ` : ''}
                {percentTotal >= 100 ? 'nothing left for the target' : 'anything left to the target'}
              </span>
            )}
          </div>
        </div>

        {contacts.length > 0 && (
          <>
            <label htmlFor="r-contact">Contact</label>
            <select
              id="r-contact"
              value={form.targetContactId}
              onChange={(e) => set('targetContactId', e.target.value)}
            >
              <option value="">— none —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </>
        )}

        {locations.length > 0 && (
          <>
            <label htmlFor="r-loc">Location</label>
            <select
              id="r-loc"
              value={form.targetLocationId}
              onChange={(e) => set('targetLocationId', e.target.value)}
            >
              <option value="">— none —</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </>
        )}

        {classes.length > 0 && (
          <>
            <label htmlFor="r-cls">Class</label>
            <select
              id="r-cls"
              value={form.targetClassId}
              onChange={(e) => set('targetClassId', e.target.value)}
            >
              <option value="">— none —</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </>
        )}

        <label htmlFor="r-memo">Memo template</label>
        <input
          id="r-memo"
          value={form.memoTemplate}
          onChange={(e) => set('memoTemplate', e.target.value)}
          placeholder="e.g. Office supplies {MONTH} {YYYY}"
        />

        <label htmlFor="r-auto">Auto-post</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input
              id="r-auto"
              type="checkbox"
              checked={form.autoPost}
              onChange={(e) => set('autoPost', e.target.checked)}
            />
            Create the journal entry automatically when this rule matches
          </label>
          {form.autoPost && (
            <label
              style={{
                display: 'inline-flex',
                gap: 6,
                alignItems: 'center',
                fontSize: 13,
                marginLeft: 18,
              }}
            >
              Require review above:
              <input
                type="text"
                inputMode="decimal"
                value={form.requireReviewAboveAmount}
                onChange={(e) => set('requireReviewAboveAmount', e.target.value)}
                placeholder="(no ceiling)"
                style={{ width: 120 }}
              />
            </label>
          )}
        </div>
      </div>

      {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          type="submit"
          className="btn-primary"
          disabled={mutation.isPending || !form.name.trim() || !form.targetAccountId || !splitsValid}
        >
          {mutation.isPending
            ? isEditing ? 'Saving…' : 'Creating…'
            : isEditing ? 'Save changes' : 'Create rule'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
